import { Injectable, computed, signal } from '@angular/core';
import type { Business } from '../models/business.model';

@Injectable({ providedIn: 'root' })
export class BusinessContextService {
  // Arranca vacío: sin sesión restaurada todavía (ver AuthService.restoreSession()) no hay
  // negocio activo — antes arrancaba con un negocio quemado, consistente con el usuario mock
  // "siempre logueado" que también se eliminó de AuthService.
  // activeBusinessId es estado propio de este service (no un campo del wire), por eso queda
  // camelCase como el resto de la API en TypeScript del servicio.
  private readonly _businesses = signal<Business[]>([]);
  private readonly _activeBusinessId = signal<string | null>(null);

  readonly businesses = this._businesses.asReadonly();
  readonly activeBusinessId = this._activeBusinessId.asReadonly();
  readonly activeBusiness = computed(
    () => this._businesses().find((business) => business.id === this._activeBusinessId()) ?? null,
  );

  // Nunca confiar en un businessId que venga del body/query — solo el de esta señal
  // (equivalente frontend de "siempre usar el businessId de la ruta activa", CLAUDE.md §1).
  setActiveBusiness(businessId: string): void {
    this._activeBusinessId.set(businessId);
  }

  // Lo llama AuthService tras login/register/me — reemplaza la lista completa (no hay merge
  // parcial porque login/register/me siempre traen el estado completo, nunca un delta).
  setBusinesses(businesses: Business[]): void {
    this._businesses.set(businesses);
  }

  // Lo llama features/settings/ tras PATCH /businesses/:id o /bot-config — un solo negocio se
  // reemplaza en la lista (a diferencia de setBusinesses, que reemplaza todo). Fuente única de
  // verdad del negocio activo: settings.service.ts nunca mantiene su propia copia mutable, así
  // el resto de la app (header, sidebar, cualquier feature que lea activeBusiness()) ve el
  // cambio de inmediato sin recargar nada.
  updateBusiness(business: Business): void {
    this._businesses.update((list) => list.map((existing) => (existing.id === business.id ? business : existing)));
  }

  clear(): void {
    this._businesses.set([]);
    this._activeBusinessId.set(null);
  }
}
